import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * Props for the QuotaIndicator component
 */
export interface QuotaIndicatorProps {
  /** Number of remaining searches */
  remaining: number;
  /** Total daily search limit */
  total: number;
  /** Whether the user has premium subscription */
  isPremium: boolean;
  /** Show upgrade link when quota is low (default: true) */
  showUpgradeLink?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Threshold for warning state (yellow) - warning when remaining <= 5
 */
const WARNING_THRESHOLD = 5;

/**
 * Threshold for critical state (red) - alert when remaining <= 2
 */
const CRITICAL_THRESHOLD = 2;

/**
 * QuotaIndicator Component
 *
 * Displays the remaining daily search quota for free users.
 * Shows different visual styles based on remaining searches:
 * - Normal (green): > 5 remaining
 * - Warning (yellow): <= 5 remaining (but > 2)
 * - Critical (red): <= 2 remaining
 *
 * The component is hidden for Premium and Admin users since
 * they have unlimited searches.
 *
 * @validates Requirements 8.7
 *
 * @example
 * ```tsx
 * <QuotaIndicator remaining={15} total={20} isPremium={false} />
 * ```
 */
export function QuotaIndicator({
  remaining,
  total,
  isPremium,
  showUpgradeLink = true,
  className = '',
}: QuotaIndicatorProps) {
  const { t } = useTranslation();

  // Hide for Premium/Admin users - they have unlimited searches
  if (isPremium) {
    return null;
  }

  // Determine the visual state based on remaining searches
  // Warning: remaining <= 5 (5, 4, 3)
  // Critical: remaining <= 2 (2, 1, 0)
  const isCritical = remaining <= CRITICAL_THRESHOLD;
  const isWarning = remaining <= WARNING_THRESHOLD && !isCritical;

  // Container styles based on state
  const containerStyles = getContainerStyles(isCritical, isWarning);

  // Icon and text styles based on state
  const iconStyles = getIconStyles(isCritical, isWarning);
  const textStyles = getTextStyles(isCritical, isWarning);

  // Determine if we should show the upgrade link
  const shouldShowUpgradeLink = showUpgradeLink && (isCritical || isWarning);

  // Get translated text with interpolation
  const ariaLabelText = t('search.quota.ariaLabel')
    .replace('{remaining}', String(remaining))
    .replace('{total}', String(total));

  const remainingText = t('search.quota.remaining')
    .replace('{remaining}', String(remaining))
    .replace('{total}', String(total));

  return (
    <div
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg text-sm
        transition-all duration-200
        ${containerStyles}
        ${className}
      `.trim()}
      role="status"
      aria-live="polite"
      aria-label={ariaLabelText}
    >
      {/* Status icon */}
      <QuotaIcon
        isCritical={isCritical}
        isWarning={isWarning}
        className={iconStyles}
      />

      {/* Quota text */}
      <span className={textStyles}>
        {remainingText}
      </span>

      {/* Upgrade link when quota is low */}
      {shouldShowUpgradeLink && (
        <Link
          to="/subscription"
          className={`
            ml-2 px-2 py-1 rounded-md text-xs font-medium
            transition-all duration-150
            ${isCritical
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-yellow-600 text-white hover:bg-yellow-700'
            }
          `.trim()}
        >
          {t('search.quota.upgradePremium')}
        </Link>
      )}
    </div>
  );
}

/**
 * Get container styles based on quota state
 */
function getContainerStyles(isCritical: boolean, isWarning: boolean): string {
  if (isCritical) {
    return 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800';
  }
  if (isWarning) {
    return 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800';
  }
  return 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800';
}

/**
 * Get icon styles based on quota state
 */
function getIconStyles(isCritical: boolean, isWarning: boolean): string {
  if (isCritical) {
    return 'text-red-500 dark:text-red-400';
  }
  if (isWarning) {
    return 'text-yellow-500 dark:text-yellow-400';
  }
  return 'text-emerald-500 dark:text-emerald-400';
}

/**
 * Get text styles based on quota state
 */
function getTextStyles(isCritical: boolean, isWarning: boolean): string {
  if (isCritical) {
    return 'text-red-700 dark:text-red-400';
  }
  if (isWarning) {
    return 'text-yellow-700 dark:text-yellow-400';
  }
  return 'text-emerald-700 dark:text-emerald-400';
}

/**
 * Props for the QuotaIcon component
 */
interface QuotaIconProps {
  isCritical: boolean;
  isWarning: boolean;
  className?: string;
}

/**
 * Icon component that changes based on quota state
 */
function QuotaIcon({ isCritical, isWarning, className = '' }: QuotaIconProps) {
  // Critical: exclamation icon
  if (isCritical) {
    return (
      <svg
        className={`w-4 h-4 ${className}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    );
  }

  // Warning: clock icon
  if (isWarning) {
    return (
      <svg
        className={`w-4 h-4 ${className}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }

  // Normal: check circle icon
  return (
    <svg
      className={`w-4 h-4 ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export default QuotaIndicator;
